import { useEffect, useState } from 'react';
import { useSocket } from '@/contexts/SocketContext';

interface ChannelConnectionStatus {
  isConnected: boolean;
  qrCode?: string;
  expires?: number;
  error?: string;
}

/**
 * Hook para monitorar o status de conexão de um canal via Socket.IO
 *
 * @param instanceKey - Chave da instância do canal (para Gateway providers)
 * @returns Status da conexão, QR Code e informações de expiração
 *
 * @example
 * const { isConnected, qrCode, expires } = useChannelConnection('my-instance-key');
 *
 * if (qrCode) {
 *   // Exibir QR Code para escaneamento
 * }
 *
 * if (isConnected) {
 *   // Canal conectado com sucesso
 * }
 */
export const useChannelConnection = (instanceKey?: string) => {
  const { socket, isConnected: socketConnected } = useSocket();
  const [status, setStatus] = useState<ChannelConnectionStatus>({
    isConnected: false
  });

  useEffect(() => {
    if (!socket || !socketConnected || !instanceKey) {
      return;
    }

    // Event handler for channel connection status updates
    const handleChannelConnect = (data: any) => {
      if (data.instanceKey === instanceKey) {
        setStatus({
          isConnected: true,
          qrCode: undefined,
          expires: undefined,
          error: undefined
        });
      }
    };

    // Event handler for channel disconnection
    const handleChannelDisconnect = (data: any) => {
      if (data.instanceKey === instanceKey) {
        setStatus({
          isConnected: false,
          error: data.reason || 'Channel disconnected'
        });
      }
    };

    // Event handler for QR code updates
    const handleQRCode = (data: any) => {
      if (data.instanceKey === instanceKey) {
        setStatus({
          isConnected: false,
          qrCode: data.qrCode,
          expires: data.expires,
          error: undefined
        });
      }
    };

    // Event handler for connection errors
    const handleConnectionError = (data: any) => {
      console.error(`❌ Channel connection error:`, data);

      if (data.instanceKey === instanceKey) {
        setStatus({
          isConnected: false,
          error: data.error || 'Connection failed'
        });
      }
    };

    // Register event listeners
    socket.on(`channel:${instanceKey}:connect`, handleChannelConnect);
    socket.on(`channel:${instanceKey}:disconnect`, handleChannelDisconnect);
    socket.on(`channel:${instanceKey}:qr`, handleQRCode);
    socket.on(`channel:${instanceKey}:error`, handleConnectionError);

    // Cleanup function
    return () => {
      socket.off(`channel:${instanceKey}:connect`, handleChannelConnect);
      socket.off(`channel:${instanceKey}:disconnect`, handleChannelDisconnect);
      socket.off(`channel:${instanceKey}:qr`, handleQRCode);
      socket.off(`channel:${instanceKey}:error`, handleConnectionError);
    };
  }, [socket, socketConnected, instanceKey]);

  return status;
};
